import { SendDirectMessagePayload } from "../types/sendMessagePayload";

type EventHandler = (...args: any[]) => void;

class WebSocketManager {
    private ws: WebSocket | null = null;
    private eventHandlers: Map<string, EventHandler[]> = new Map();
    private isConnected = false; // Connection status remains private
    private reconnectAttempts = 0;
    private readonly maxReconnectAttempts = 5;
    private readonly reconnectInterval = 3000; // in ms
    private id: string | null = null; // User phone number
    private url: string | null = null;

    // Active subscriptions for real-time updates
    private activeSubscriptions: Set<string> = new Set();

    // Public getter to expose connection status
    public get connectionStatus(): boolean {
        return this.isConnected;
    }

    // Initialize WebSocket connection with server URL and phone number
    public initialize({ url, id }: { url: string; id: string }): void {
        if (this.ws) {
            console.warn("WebSocket is already initialized");
            return;
        }

        this.url = url;
        this.id = id;

        this.connect(); // Establish connection
    }

    // Establish WebSocket connection
    private connect(): void {
        if (!this.url || !this.id) {
            console.error("WebSocket URL or phone number (id) is missing");
            return;
        }

        this.ws = new WebSocket(`${this.url}`); // Initialize WebSocket connection

        this.ws.onopen = () => {
            console.log("WebSocket connected");
            this.isConnected = true;
            this.reconnectAttempts = 0;

            // Immediately send `join` message with user phone number (id)
            this.sendJoinMessage();

            // Resubscribe to all active subscriptions
            this.resubscribeToActiveSubscriptions();

            // Trigger "connect" event
            this.triggerEvent("connect");
        };

        this.ws.onmessage = (event: MessageEvent) => {
            try {
                const data = JSON.parse(event.data);
                console.log("Message from WebSocket server:", data);

                // Trigger "message" event with received data
                this.triggerEvent("message", data);
            } catch (error) {
                console.error("Error parsing WebSocket message:", error);
            }
        };

        this.ws.onclose = () => {
            console.warn("WebSocket connection closed");
            this.isConnected = false;

            // Trigger "disconnect" event
            this.triggerEvent("disconnect");

            // Attempt to reconnect
            this.reconnect();
        };

        this.ws.onerror = (error) => {
            console.error("WebSocket encountered an error:", error);
        };
    }

    // Send a `join` message to the server with the user's phone number
    private sendJoinMessage(): void {
        if (this.ws && this.isConnected && this.ws.readyState === WebSocket.OPEN) {
            const joinMessage = { type: "join", phoneNumber: this.id };
            this.ws.send(JSON.stringify(joinMessage));
            console.log("Sent join message with phone number:", joinMessage);
        } else {
            console.warn("WebSocket is not ready. Unable to send join message.");
        }
    }

    // Subscribe to real-time updates for specific events
    public subscribe(event: string): void {
        if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
            const subscriptionMessage = { type: "subscribe", event };
            this.ws.send(JSON.stringify(subscriptionMessage));
            console.log(`Subscribed to event: ${event}`);

            // Add to active subscriptions
            this.activeSubscriptions.add(event);
        } else {
            console.warn("WebSocket is not connected. Cannot subscribe.");
        }
    }

    // Unsubscribe from real-time updates for specific events
    public unsubscribe(event: string): void {
        if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
            const unsubscribeMessage = { type: "unsubscribe", event };
            this.ws.send(JSON.stringify(unsubscribeMessage));
            console.log(`Unsubscribed from event: ${event}`);

            // Remove from active subscriptions
            this.activeSubscriptions.delete(event);
        } else {
            console.warn("WebSocket is not connected. Cannot unsubscribe.");
        }
    }

    // Resubscribe to all active subscriptions on reconnect
    private resubscribeToActiveSubscriptions(): void {
        this.activeSubscriptions.forEach((event) => this.subscribe(event));
    }

    // Send a direct message to another user
    public sendDirectMessage(
        sender: {
            email: string;
            phoneNumber: string;
            firstName: string;
            lastName: string;
            profilePicture?: string | null;
        }, // Sender's object with all necessary details
        recipientIdentifier: string, // Recipient's unique identifier
        content: string, // Message content
        fileType: string | null = null, // Optional: File type (image, video, etc.)
        fileUrl: string | null = null // Optional: File URL for attachments
    ): void {
        if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
            const messagePayload: SendDirectMessagePayload = {
                sender, // Sender object with all details (email, phoneNumber, etc.)
                recipientIdentifier, // Recipient's identifier (email or phone number)
                message: content, // Message content
                fileType, // Optional: File type (image, video, etc.)
                fileUrl, // Optional: File URL for attachments
            };

            this.ws.send(JSON.stringify(messagePayload));
            console.log("Sent direct message:", messagePayload);
        } else {
            console.warn("WebSocket is not connected. Direct message not sent.");
        }
    }

    // WebSocket reconnection logic
    private reconnect(): void {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error("Maximum reconnection attempts reached");
            return;
        }

        this.reconnectAttempts++;
        console.log(`Reconnecting WebSocket... Attempt ${this.reconnectAttempts}`);

        setTimeout(() => {
            this.connect();
        }, this.reconnectInterval);
    }

    // Subscribe to WebSocket events
    public on(event: string, handler: EventHandler): void {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event)?.push(handler);
    }

    // Unsubscribe from WebSocket events
    public off(event: string, handler: EventHandler): void {
        if (!this.eventHandlers.has(event)) return;

        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            this.eventHandlers.set(
                event,
                handlers.filter((h) => h !== handler)
            );
        }
    }

    // Trigger WebSocket events
    private triggerEvent(event: string, ...args: any[]): void {
        const handlers = this.eventHandlers.get(event);
        if (handlers) {
            handlers.forEach((handler) => handler(...args));
        }
    }

    // Close the WebSocket connection
    public close(): void {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
            this.isConnected = false;
            console.log("WebSocket connection closed.");
        }
    }
}

// Singleton WebSocketManager instance
const webSocketManager = new WebSocketManager();
export default webSocketManager;
